import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { meetingOutcomes, meetingSteps } from '../../data/site';
const toneClasses = {
  clay: 'bg-clay',
  sage: 'bg-sage',
  sand: 'bg-sand'
} as const;
const EASE = [0.23, 1, 0.32, 1] as const;
export function MeetingLoop() {
  const reduceMotion = useReducedMotion();

  /** The loop is sequential, so the steps arrive in order. Capped at four. */
  const stepMotion = (index: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.6
    },
    transition: {
      duration: 0.26,
      ease: EASE,
      delay: index * 0.05
    }
  };
  return <section id="meetings" className="bg-charcoal-700" data-id="element-194" data-mp="18oz1t79bufiv">
      <div className="mx-auto max-w-[1180px] px-5 py-20 sm:px-8 lg:py-28" data-id="element-195" data-mp="37yyqzl9rmum0">
        <div className="max-w-[720px]" data-id="element-196" data-mp="330dqrluikaxk">
          <p className="micro-label text-[9px] text-gold-400" data-id="element-197" data-mp="331gh9snxcbcy">The meeting loop</p>
          <h2 className="mt-4 font-display text-[32px] font-semibold leading-[1.08] tracking-[-0.03em] text-cream sm:text-[40px]" data-id="element-198" data-mp="3tlwoop3qm6kp">
            From meeting to updated opening plan - without the admin in between.
          </h2>
          <p className="mt-5 text-[15.5px] leading-relaxed text-charcoal-200" data-id="element-199" data-mp="3ko49rm1xh0xu">
            Most of the real work happens in conversations, decisions and corridor calls - not inside
            a project-management interface. KeyTurn closes the gap between the discussion and the
            plan, so the meeting produces outcomes instead of homework.
          </p>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16" data-id="element-200" data-mp="7eyfxt8db53c">
          <ol className="relative" data-id="element-201" data-mp="cdjfdqfspkqq">
            <span className="absolute bottom-6 left-[7px] top-3 w-px bg-white/15" aria-hidden="true" data-id="element-202" data-mp="cchuyvcw3lwc" />
            {meetingSteps.map((step, index) => <motion.li key={step.step} className="relative pb-9 pl-9 last:pb-0" {...stepMotion(index)} data-id="element-203" data-mp="14wyjlh77d517" data-mpi={index}>
                <span className="absolute left-0 top-[5px] h-[15px] w-[15px] rounded-full border-2 border-charcoal-700 bg-gold-400" aria-hidden="true" data-id="element-204" data-mp="19skgudwsgx6q" />
                <p className="micro-label text-[9px] text-charcoal-300" data-id="element-205" data-mp="ztbsq1olucsa">{step.step}</p>
                <h3 className="mt-2 font-display text-[19px] font-medium leading-snug tracking-[-0.02em] text-cream" data-id="element-206" data-mp="vt1k0h9fh89q">
                  {step.title}
                </h3>
                <p className="mt-2.5 max-w-[56ch] text-[13.5px] leading-relaxed text-charcoal-200" data-id="element-207" data-mp="m1x6llzehvh0">
                  {step.body}
                </p>
              </motion.li>)}
          </ol>

          <Reveal data-id="element-208" data-mp="c4iozj1qr0n4">
            <div className="rounded-2xl border border-white/10 bg-charcoal-800 p-5 sm:p-6" data-id="element-209" data-mp="c2z9vvrtmvmx">
              <p className="micro-label text-[9px] text-charcoal-300" data-id="element-210" data-mp="83qgwt4lv1sf">Proposed changes</p>
              <p className="mt-2 text-[12.5px] leading-relaxed text-charcoal-400" data-id="element-211" data-mp="2jqsnmcjvt0rb">
                Licence escalation · 24 minutes · 5 attendees
              </p>

              <ul className="mt-4 divide-y divide-white/[0.07] border-t border-white/[0.07]" data-id="element-212" data-mp="2sqpemktqubpj">
                {meetingOutcomes.map(outcome => <li key={outcome.detail} className="flex items-start gap-3 py-3.5" data-id="element-213" data-mp="2xoh6xfyavzu9">
                    <span className={`mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full ${toneClasses[outcome.tone]}`} aria-hidden="true" data-id="element-214" data-mp="20330iimw3oof" />
                    <div className="min-w-0" data-id="element-215" data-mp="29y4q1fl2usrj">
                      <p className="micro-label text-[8.5px] text-charcoal-400" data-id="element-216" data-mp="2er7jzotro489">{outcome.kind}</p>
                      <p className="mt-1 text-[13px] leading-snug text-cream" data-id="element-217" data-mp="1hcbthnqdkad7">{outcome.detail}</p>
                    </div>
                  </li>)}
              </ul>

              <p className="mt-5 rounded-lg bg-gold-400 px-4 py-2.5 text-center text-[12.5px] font-semibold text-charcoal-800" data-id="element-218" data-mp="3nzj6ywyel7nv">
                Apply reviewed outcomes
              </p>
            </div>

            <p className="mt-6 font-display text-[17px] font-medium leading-snug tracking-[-0.02em] text-gold-400" data-id="element-219" data-mp="1kri1f0s2z9d">
              You run the meeting. KeyTurn closes the loop.
            </p>
          </Reveal>
        </div>
      </div>
    </section>;
}